import dayjs from 'dayjs'
import { billingPeriodLabel, billingProductName, billingSuggestedAmount, billingServiceDescription } from '@/helpers/studentBilling'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Button, Checkbox, Dropdown, Input, InputNumber, Modal, Table, Tooltip, message } from 'antd'
import { CheckCircleFilled, EllipsisOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import Select from '@UI/New/Select'
import NewButton from '@UI/New/Button'
import { selectProfile } from '@store/profile.reducer'
import { getBaseRole } from '@/helpers/customRoles'
import { studentBillingApi as api } from '@/api/queries/studentBilling'
import s from './index.module.css'

const units = [
  { value: 'months', label: 'Месяцы' }, { value: 'weeks', label: 'Недели' },
  { value: 'days', label: 'Дни' }, { value: 'dates', label: 'Точные даты' },
]
const errorMessage = (error) => message.error(error?.response?.data?.error || error.message || 'Не удалось выполнить операцию')
const money = (value) => Number(value).toLocaleString('ru-RU', { style: 'currency', currency: 'RUB' })

const Choice = ({ value, onChange, options, placeholder, compact = false, ...props }) => (
  <Select compact={compact} fields={{ value, onChange, options, placeholder, style: { minWidth: compact ? 0 : 190, width: '100%' }, ...props }} />
)

const InvoiceForm = ({ userId, companies, onCreated, onCancel }) => {
  const [students, setStudents] = useState([])
  const [products, setProducts] = useState([])
  const [student, setStudent] = useState(userId)
  const [productKey, setProductKey] = useState()
  const [company, setCompany] = useState(0)
  const [unit, setUnit] = useState('months')
  const [value, setValue] = useState(1)
  const [from, setFrom] = useState('')
  const [until, setUntil] = useState('')
  const [amount, setAmount] = useState(0)
  const [description, setDescription] = useState('')
  const [descriptionEdited, setDescriptionEdited] = useState(false)
  const [busy, setBusy] = useState(false)
  const key = useRef(`manual:${Date.now()}:${Math.random().toString(36).slice(2)}`)
  const searchSequence = useRef(0)
  const findStudents = useCallback(async (search) => {
    const sequence = ++searchSequence.current
    try {
      const result = await api.students(search)
      if (sequence === searchSequence.current) setStudents(result)
    } catch (error) { errorMessage(error) }
  }, [])
  useEffect(() => { if (!userId) findStudents('') }, [userId, findStudents])
  useEffect(() => {
    let active = true
    setProducts([])
    setProductKey(undefined)
    if (student) api.products(student).then((result) => { if (active) setProducts(result) }).catch(errorMessage)
    return () => { active = false }
  }, [student])
  const product = products.find((item) => `${item.target_type}:${item.target_id}` === productKey)
  useEffect(() => { setAmount(billingSuggestedAmount(product, unit, value, from, until)) }, [product, unit, value, from, until])
  useEffect(() => {
    if (!descriptionEdited) setDescription(billingServiceDescription(product, unit, value, from, until))
  }, [product, unit, value, from, until, descriptionEdited])
  const selectProduct = (next) => {
    const item = products.find((p) => `${p.target_type}:${p.target_id}` === next)
    setProductKey(next)
    if (!item) return
    setDescriptionEdited(false)
    setUnit(item.default_unit || (item.by_lessons ? 'lessons' : 'months'))
    setValue(item.default_value || 1)
  }
  const submit = async () => {
    setBusy(true)
    try {
      await api.create({ user_id: student, target_type: product.target_type, target_id: product.target_id,
        company_id: company, period_unit: unit, period_value: value, period_from: from, period_until: until,
        amount, description, request_key: key.current })
      onCreated()
    } catch (error) { errorMessage(error) } finally { setBusy(false) }
  }
  return (
    <Modal open title="Выставить счёт" onCancel={onCancel} onOk={submit} confirmLoading={busy}
      width={640} className={s.dialog} okText="Создать счёт" cancelText="Отмена" okButtonProps={{ disabled: !student || !product || !amount || !companies.length || (unit === 'dates' && (!from || !until || until < from)) }}>
      <div className={s.form}>
        {!userId && <label>Ученик<Choice value={student} onChange={setStudent} placeholder="Найти ученика" showSearch filterOption={false}
          onSearch={findStudents} options={students.map((item) => ({ value: Number(item.id), label: item.name }))} /></label>}
        <label>Курс или класс<Choice value={productKey} onChange={selectProduct} placeholder="Выберите продукт"
          options={products.map((item) => ({ value: `${item.target_type}:${item.target_id}`, label: item.target_type === 'class' ? billingProductName(item) : item.label }))} /></label>
        <label>Компания<Choice value={company} onChange={setCompany} options={companies.map((item) => ({ value: item.id, label: `${item.company_name || 'Основная компания'}${item.is_primary ? ' (основная)' : ''}` }))} /></label>
        <label>Период оплаты<Choice value={unit} onChange={setUnit} options={product?.by_lessons ? [{ value: 'lessons', label: 'Занятия' }] : units} /></label>
        {unit === 'dates' ? <div className={s.filters}>
          <label>С<Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
          <label>По<Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} /></label>
        </div> : <label>{unit === 'lessons' ? 'Количество занятий' : 'Количество периодов'}<InputNumber min={1} max={10000} precision={0} value={value} onChange={setValue} /></label>}
        {product && <div className={s.formSummary}><b>{billingProductName(product)}</b><span>{billingPeriodLabel(unit, value, from, until) || 'Укажите период оплаты'}</span></div>}
        <label>Сумма, ₽<InputNumber min={0.01} max={9999999999.99} precision={2} value={amount} onChange={setAmount} /></label>
        <label>Наименование услуги<Input.TextArea value={description} onChange={(e) => { setDescriptionEdited(true); setDescription(e.target.value) }} autoSize={{ minRows: 2, maxRows: 5 }} placeholder="По умолчанию: продукт и период оплаты" /></label>
        {!companies.some((item) => item.id === company && item.is_complete && item.vat_type) && <Alert type="warning" message="Заполните реквизиты и НДС выбранной компании в настройках → О компании." />}
      </div>
    </Modal>
  )
}

const PaymentModal = ({ row, onDone, onCancel }) => {
  const paid = row.status !== 'paid'
  const [preview, setPreview] = useState(null)
  const [previewError, setPreviewError] = useState('')
  const [skip, setSkip] = useState(false)
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    let active = true
    api.preview(row.id, paid).then((result) => { if (active) setPreview(result) })
      .catch((error) => { if (active) setPreviewError(error?.response?.data?.error || error.message) })
    return () => { active = false }
  }, [row.id, paid])
  const submit = async () => {
    setBusy(true)
    try { await api.setPaid(row.id, paid, skip); onDone() } catch (error) { errorMessage(error) } finally { setBusy(false) }
  }
  return <Modal open title={paid ? 'Подтвердить оплату' : 'Отменить оплату'} onCancel={onCancel} onOk={submit}
    className={s.dialog} confirmLoading={busy} okText="Подтвердить" cancelText="Отмена" okButtonProps={{ disabled: !skip && !preview }}>
    <div className={s.paymentSummary}><span>Счёт № {row.id}<small>{billingProductName(row.product)}</small></span><b>{money(row.amount)}</b></div>
    <Alert type={!skip && previewError ? 'warning' : 'info'} message={skip ? 'Будет изменён только статус оплаты. Доступ и баланс останутся прежними.' : previewError || preview?.message || 'Расчёт изменения доступа…'} />
    <p><Checkbox checked={skip} onChange={(e) => setSkip(e.target.checked)}>
      {paid ? 'Только отметить оплату, без продления доступа и пополнения баланса' : 'Только отменить статус оплаты, без изменения доступа и баланса'}
    </Checkbox></p>
  </Modal>
}

const StudentBilling = ({ userId, onPaymentChange, showTitle = !userId }) => {
  const profile = useSelector(selectProfile)
  const manager = ['admin', 'junior-admin'].includes(getBaseRole(profile?.role))
  const [data, setData] = useState({ items: [], total: 0 })
  const [filters, setFilters] = useState({})
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [companies, setCompanies] = useState([])
  const [creating, setCreating] = useState(false)
  const [payment, setPayment] = useState(null)
  const [revision, setRevision] = useState(0)
  const [actionBusy, setActionBusy] = useState(false)
  const [menuRow, setMenuRow] = useState(null)
  const refresh = () => setRevision((n) => n + 1)
  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    api.list({ ...filters, ...(userId ? { user_id: userId } : {}), page }).then((result) => { if (active) setData(result) })
      .catch((e) => { if (active) setError(e?.response?.data?.error || e.message) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [userId, filters, page, revision])
  useEffect(() => {
    if (manager) api.companies().then(setCompanies).catch(errorMessage)
  }, [manager])
  const filter = (name, value) => { setPage(1); setFilters((current) => ({ ...current, [name]: value })) }
  const download = async (row, type) => {
    try {
      const response = await api.download(row.id, type)
      const url = URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = url
      link.download = `${type === 'act' ? 'УПД' : 'Счёт'}-${row.id}.pdf`
      link.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (e) { errorMessage(e) }
  }
  const mutate = async (operation) => {
    setActionBusy(true)
    try { await operation(); refresh() } catch (e) { errorMessage(e) } finally { setActionBusy(false) }
  }
  const columns = [
    { title: 'Счёт / дата', width: 145, dataIndex: 'created_at', render: (value, row) => <div className={s.documentNumber}><b>№ {row.id}</b><small>{value ? dayjs(value).format('DD.MM.YYYY') : '—'}</small></div> },
    ...(!userId ? [{ title: 'Ученик', render: (_, row) => <span>{row.product.student_name}<br /><small>ID {row.user_id}</small></span> }] : []),
    { title: 'Продукт', width: 280, render: (_, row) => <div className={s.product}><b>{billingProductName(row.product)}</b><span>{[row.product.tariff, row.product.group || row.product.teacher].filter(Boolean).join(' · ')}</span><small>{row.description}</small></div> },
    { title: 'Период', width: 170, render: (_, row) => billingPeriodLabel(row.period_unit, row.period_value, row.period_from, row.period_until) || row.period_label },
    { title: 'Сумма', width: 150, dataIndex: 'amount', render: (value, row) => <div className={s.amountCell}><b className={s.amount}>{money(value)}</b>
      {row.status === 'paid' && typeof row.has_payment_effect === 'boolean' && <small>{row.has_payment_effect ? 'Начислено' : 'Без начисления'}</small>}
    </div> },
    { title: 'Создан', dataIndex: 'source', render: (value) => value === 'notification' ? 'Уведомление' : 'Вручную' },
    { title: 'Счёт', render: (_, row) => <div className={s.actions}>
      <Button size="small" onClick={() => download(row, 'invoice')}>PDF</Button>
      <Tooltip title={row.status === 'paid' ? 'Оплачен' : 'Не оплачен'}>
        {manager ? <Button type="text" className={`${s.paymentStatus} ${row.status === 'paid' ? s.paymentStatusPaid : ''}`}
          aria-label={`${row.status === 'paid' ? 'Отменить' : 'Отметить'} оплату счёта № ${row.id}`}
          disabled={actionBusy} onClick={() => setPayment(row)}><CheckCircleFilled /></Button>
          : <span className={`${s.paymentStatus} ${row.status === 'paid' ? s.paymentStatusPaid : ''}`}
            aria-label={`Статус оплаты: ${row.status === 'paid' ? 'оплачен' : 'не оплачен'}`}><CheckCircleFilled /></span>}
      </Tooltip>
    </div> },
    { title: 'Акт / УПД', render: (_, row) => row.act_created_at
      ? <Button size="small" onClick={() => download(row, 'act')}>PDF</Button>
      : manager && <Button size="small" disabled={actionBusy} onClick={() => Modal.confirm({ title: 'Создать УПД на полную сумму счёта?', content: `${row.product.label} · ${money(row.amount)}`, okText: 'Создать', cancelText: 'Отмена', onOk: () => mutate(() => api.createAct(row.id)) })}>+ Создать</Button> },
    ...(manager ? [{ title: 'Действия', render: (_, row) => <Dropdown trigger={['click']} open={menuRow === row.id} onOpenChange={(open) => setMenuRow(open ? row.id : null)} menu={{
      items: [
        { key: 'payment', label: row.status === 'paid' ? 'Отменить оплату' : 'Отметить оплату', disabled: actionBusy },
        { key: 'delete', label: <span title={row.status === 'paid' ? 'Сначала отмените статус оплаты счёта' : ''}>Удалить</span>, danger: true, disabled: actionBusy || row.status === 'paid' },
      ],
      onClick: ({ key }) => {
        setMenuRow(null)
        if (key === 'payment') setPayment(row)
        if (key === 'delete' && row.status !== 'paid') Modal.confirm({ title: `Удалить неоплаченный счёт № ${row.id}?`, okText: 'Удалить', cancelText: 'Отмена', onOk: () => mutate(() => api.remove(row.id)) })
      },
    }}><Button size="small" aria-label={`Действия со счётом № ${row.id}`}><EllipsisOutlined /></Button></Dropdown> }] : []),
  ]
  return <section className={s.ledger}>
    <div className={s.toolbar}>{showTitle && <h2>Счета и акты</h2>}{manager && <NewButton onClick={() => setCreating(true)}>Выставить счёт</NewButton>}</div>
    <div className={`${s.filters} ${s.reportFilters}`}>
      <label>С<Input type="date" value={filters.from || ''} onChange={(e) => filter('from', e.target.value)} /></label>
      <label>По<Input type="date" value={filters.until || ''} onChange={(e) => filter('until', e.target.value)} /></label>
      <label>Статус<Choice compact value={filters.status} onChange={(value) => filter('status', value)} allowClear placeholder="Все статусы" options={[{ value: 'issued', label: 'Не оплачен' }, { value: 'paid', label: 'Оплачен' }]} /></label>
      <label>Акт<Choice compact value={filters.has_act} onChange={(value) => filter('has_act', value)} allowClear placeholder="Все счета" options={[{ value: '1', label: 'С актом' }, { value: '0', label: 'Без акта' }]} /></label>
      <label>Создан<Choice compact value={filters.source} onChange={(value) => filter('source', value)} allowClear placeholder="Все источники" options={[{ value: 'manual', label: 'Вручную' }, { value: 'notification', label: 'Уведомление' }]} /></label>
      <label className={s.searchFilter}>Продукт / ученик<Input.Search placeholder="Название или имя" allowClear onSearch={(value) => filter('search', value)} /></label>
      {manager && <label>Компания<Choice compact value={filters.company_id} onChange={(value) => filter('company_id', value)} allowClear placeholder="Все компании" options={companies.map((item) => ({ value: item.id, label: item.company_name || 'Основная компания' }))} /></label>}
    </div>
    {error && <Alert type="error" message={error} />}
    <Table rowKey="id" columns={columns} dataSource={data.items} loading={loading} scroll={{ x: 1100 }}
      locale={{ emptyText: 'Счетов пока нет' }} pagination={{ current: page, pageSize: 30, total: data.total, hideOnSinglePage: true, showTotal: (total) => `Всего счетов: ${total}`, showSizeChanger: false, onChange: setPage }} />
    {creating && <InvoiceForm userId={userId} companies={companies} onCancel={() => setCreating(false)} onCreated={() => { setCreating(false); setPage(1); refresh() }} />}
    {payment && <PaymentModal row={payment} onCancel={() => setPayment(null)} onDone={() => { setPayment(null); refresh(); onPaymentChange?.() }} />}
  </section>
}

export const StudentBillingButton = ({ userId }) => {
  const profile = useSelector(selectProfile)
  const role = getBaseRole(profile?.role)
  const manager = ['admin', 'junior-admin'].includes(role)
  const allowed = manager || ['children', 'parent'].includes(role)
  const [count, setCount] = useState(0)
  const [open, setOpen] = useState(false)
  useEffect(() => {
    let active = true
    setCount(0)
    setOpen(false)
    if (allowed && !manager && userId) api.list({ user_id: userId, page: 1 }).then((result) => { if (active) setCount(result.total) }).catch(() => {})
    return () => { active = false }
  }, [allowed, manager, userId])
  if (!userId || !allowed || (!manager && !count)) return null
  return <><Button onClick={() => setOpen(true)}>Счета и акты</Button>
    <Modal open={open} onCancel={() => setOpen(false)} footer={null} width={1300} destroyOnClose>
      {open && <StudentBilling userId={userId} />}
    </Modal></>
}

export default StudentBilling
